import * as THREE from 'three'

import BaseObject from './baseObject'
import EditorTypes from '../types'


// Editing Handle
// A handle object in the scene to display handles for the user interaction
export default class EditingHandle extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)

    this.outerRadius = options.outerRadius || 22
    this.colour = options.colour || '#FFFFFF'

    this.gridSnapDisabled = !!options.gridSnapDisabled

    this.position = new THREE.Vector2(0, 0)
    this.moveLastPoint = new THREE.Vector2(0, 0)

    this.handleGeometry = null
    this.handleObject = null
    
    this.listenMouseEvents = true

    this.sceneObject = new THREE.Object3D()
    this.updateSceneObject()
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var handleScale = 1.0 / this.editor.camera.zoom

    this.handleObject.position.set(this.position.x, this.position.y, 101)
    this.handleObject.scale.set(handleScale, handleScale, 1)  
  }


  // Update the position of the handle
  setPosition(pos) {
    this.position.set(pos.x, pos.y)
  }


  /**
   * Build the initial drawing objects to display the editing line
   */
  updateSceneObject() {
    this.clearSceneObject()

    // Outer
    this.handleGeometry = new THREE.BoxGeometry(this.outerRadius, this.outerRadius, 5)
    var handleMaterial = new THREE.MeshBasicMaterial({
      map: this.editor.resources.vertexTextureHandle,
      color: this.colour,
      transparent: true,      
    })
    this.handleObject = new THREE.Mesh(this.handleGeometry, handleMaterial)
    this.sceneObject.add(this.handleObject)
  }


  clear() {
    this.sceneObject.remove(this.handleObject)
    this.handleGeometry.dispose()
    this.handleObject = null
    this.handleGeometry = null
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData, intersectionInfo) {
    this.editor.captureEventObject(this)
    this.sendObjectEvent(EditorTypes.ObjectEventTypes.objectCaptured)

    this.moveLastPoint.copy(this.position)
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    
    // If captured, move the editing handle around
    if(this.editor.isCapturedEventObject(this)) {
      var castRay = mouseData.raycaster

      if(this.editor.getOptionSnapGridEnabled() && !this.gridSnapDisabled) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the editing handle
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var rayInt = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, rayInt)) {

        this.position.set(rayInt.x, rayInt.y)

        // Notify any listener the handle has been moved
        this.sendObjectEvent(EditorTypes.ObjectEventTypes.objectMoved, {
          position: this.position,
          moveOffset: this.position.clone().sub(this.moveLastPoint)
        })

        this.moveLastPoint.copy(this.position)

        this.editor.flagRenderRequired()
      }

    } else {
      if(this.editor.isSelectEnabled())
        this.editor.setMouseCursorHand()
    }
  }


  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    // If captured, release it & notify listener
    if(this.editor.isCapturedEventObject(this)) {

      this.sendObjectEvent(EditorTypes.ObjectEventTypes.objectReleased)

      this.editor.releaseCapturedObject()
    }
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    var radiusTest = Math.max(worldRadius, 0.1) + (this.outerRadius * this.handleObject.scale.x)

    // Create a plane for the editing handle
    // Assumes the editing shape is sitting on the plane at origin (Z = 0)
    var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(editingPlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)
    var pos2DPoint = new THREE.Vector2(this.position.x, this.position.y)

    let dist = ray2DPoint.distanceTo(pos2DPoint)
    if(dist <= radiusTest) {
      return {
        intersection: pos2DPoint,
        position: ray2DPoint,
        objectId: this.id,
        distance: dist
      }
    }

    return null
  }

}
